import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resultApi, printApi, specimenApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Modal, Button, Badge, Section, ReasonModal, Loading, Field } from "../../Components/UI/ui";
import { GATE, RESULT_STATUS_LABEL, FLAG_LABEL, rangeText, fmtDateTime, fmtDate, toDateInput, cap, openPdf, deliveryLine } from "../../lib/lab";

// One report sheet: values keyed by parameter code, flags computed server-side.
// Entry (staff+) → verify (signatory+) → publish (signatory+).
const ResultSheet = ({ id, open, onClose, onChanged }) => {
  const { can } = useAuth();
  const { toast, defaultLayout } = useApp();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});           // code → raw input
  const [comments, setComments] = useState("");
  const [sampleAt, setSampleAt] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState(null);       // server 400 details
  const [busy, setBusy] = useState(null);
  const [interp, setInterp] = useState("");
  const [reasonFor, setReasonFor] = useState(null); // 'unverify' | 'cancel'
  const [trend, setTrend] = useState(null);         // code → points[]
  const [trendBusy, setTrendBusy] = useState(false);
  const [rejection, setRejection] = useState(null); // reason from the booking's specimens when sampleRejected
  const onChangedRef = useRef(onChanged); onChangedRef.current = onChanged;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;

  const hydrate = (res) => {
    setR(res);
    const d = {};
    for (const v of res.values || []) d[v.code] = v.value == null ? "" : String(v.displayValue ?? v.value);
    setDraft(d);
    setComments(res.comments || "");
    setSampleAt(res.sampleCollectedAt ? toDateInput(res.sampleCollectedAt) : "");
    setInterp(res.interpretation || "");
    setDirty(false);
    setErrors(null);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await resultApi.get(id);
      hydrate(res);
      setRejection(null);
      if (res.sampleRejected && res.labAppointmentId) {
        try {
          const sp = await specimenApi.get(res.labAppointmentId);
          const tube = (sp.specimens || []).find((x) => x.code === res.specimenCode) || (sp.specimens || []).find((x) => x.status === "rejected");
          setRejection(tube?.rejection?.reason || "rejected");
        } catch { setRejection("rejected"); }
      }
    }
    catch (e) { toast.error(e); onCloseRef.current?.(); }
    finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { if (open) { setTrend(null); load(); } }, [open, load]);

  const params = useMemo(() => {
    const list = [...(r?.templateSnapshot?.parameters || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const groups = [];
    for (const p of list) {
      let g = groups.find((x) => x.section === (p.section || ""));
      if (!g) { g = { section: p.section || "", items: [] }; groups.push(g); }
      g.items.push(p);
    }
    return groups;
  }, [r]);
  const valueOf = (code) => (r?.values || []).find((v) => v.code === code);

  const locked = !r || ["VERIFIED", "PUBLISHED", "CANCELLED"].includes(r.status) || !!r.sampleRejected;
  const canEnter = !locked && can(...GATE.enterValues);
  const canSign = can(...GATE.verifyResults) && !r?.sampleRejected;

  const changed = (a) => { setR(a); onChangedRef.current?.(a); };

  const save = async () => {
    setSaving(true); setErrors(null);
    try {
      const values = Object.entries(draft)
        .filter(([code]) => valueOf(code)?.isCalculated !== true)
        .map(([code, value]) => ({ code, value: value === "" ? null : value }));
      const res = await resultApi.setValues(r._id, {
        values,
        comments: comments.trim(),
        sampleCollectedAt: sampleAt ? new Date(sampleAt).toISOString() : undefined,
      });
      hydrate(res);
      onChangedRef.current?.(res);
      toast.success("Values saved");
      return true;
    } catch (e) {
      const details = e?.response?.data?.details;
      setErrors(Array.isArray(details) && details.length ? details : [apiError(e)]);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    if (dirty && !(await save())) return;
    setBusy("verify");
    try {
      changed(await resultApi.verify(r._id, { interpretation: interp.trim() }));
      toast.success("Verified");
    } catch (e) {
      const missing = e?.response?.data?.missingRequired;
      toast.error(missing?.length ? `Required parameters missing: ${missing.join(", ")}` : e);
    } finally { setBusy(null); }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      const res = await resultApi.publish(r._id);
      changed(res.result || res);
      const open = res.remainingResults > 0;
      toast.success(open
        ? `Published · ${res.remainingResults} sheet${res.remainingResults === 1 ? "" : "s"} still open on this booking`
        : res.reportFileId ? "Published · full report attached to the patient's record" : "Published · report is now with the patient");
      if (res.attachError) {
        toast.error(`The report PDF could not be attached to the patient's record (${res.attachError}). Use "Attach report" on the booking to retry.`);
      }
      // Every publish also WhatsApps the current report unless the lab turned it off
      const d = res.reportDelivery;
      if (d && d.status === "sent") toast.success(deliveryLine(d));
      else if (d && d.status === "failed") toast.error(`${deliveryLine(d)} — use "Retry WhatsApp" on the booking.`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const doReason = async (text) => {
    setBusy(reasonFor);
    try {
      if (reasonFor === "unverify") { changed(await resultApi.unverify(r._id, text)); toast.success("Sent back for entry"); }
      else { changed(await resultApi.cancel(r._id, text)); toast.success("Result cancelled"); }
      setReasonFor(null);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const loadTrend = async () => {
    setTrendBusy(true);
    try {
      const codes = (r.templateSnapshot?.parameters || []).filter((p) => p.resultType !== "text").map((p) => p.code);
      const out = {};
      await Promise.all(codes.map(async (code) => {
        try {
          const t = await resultApi.trend({ patientId: r.patientId, code });
          out[code] = (t.points || []).filter((pt) => pt.labAppointmentId !== r.labAppointmentId);
        } catch { out[code] = []; }
      }));
      setTrend(out);
    } finally { setTrendBusy(false); }
  };

  const setVal = (code, v) => { setDraft((d) => ({ ...d, [code]: v })); setDirty(true); };

  const snap = r?.templateSnapshot || {};

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        title={r ? `${snap.name}${snap.shortName ? ` (${snap.shortName})` : ""} · ${r.patientName}` : "Result"}
        footer={r && (
          <>
            {r.status === "PUBLISHED" && (
              <Button size="sm" onClick={() => openPdf(printApi.resultPdfUrl(r._id, { layout: defaultLayout }))} style={{ marginRight: "auto" }}>🖨 Print this test</Button>
            )}
            {can(...GATE.cancelResult) && r.status !== "PUBLISHED" && r.status !== "CANCELLED" && (
              <Button variant="danger" size="sm" onClick={() => setReasonFor("cancel")} style={{ marginRight: "auto" }}>Cancel result</Button>
            )}
            {canEnter && <Button variant={dirty ? "navy" : "secondary"} loading={saving} onClick={save}>Save values</Button>}
            {canSign && ["ENTERED", "PENDING"].includes(r.status) && (
              <Button variant="navy" loading={busy === "verify"} onClick={verify}>Verify</Button>
            )}
            {canSign && r.status === "VERIFIED" && (
              <>
                <Button onClick={() => setReasonFor("unverify")}>Send back</Button>
                <Button variant="success" loading={busy === "publish"} onClick={publish}>Publish report</Button>
              </>
            )}
          </>
        )}
      >
        {loading || !r ? <Loading /> : (
          <>
            <div className="lab-row between">
              <div className="lab-row">
                <Badge kind={r.status}>{RESULT_STATUS_LABEL[r.status]}</Badge>
                {r.hasCritical && <Badge kind="CANCELLED">Critical</Badge>}
                {!r.hasCritical && r.hasAbnormal && <Badge kind="IN_PROCESS">Abnormal</Badge>}
                <span className="lab-muted">
                  {r.patientContext?.sex || "—"}{r.patientContext?.ageDays != null ? ` · ${Math.floor(r.patientContext.ageDays / 365)} y` : ""} · v{r.templateVersion}{snap.specimenType ? ` · ${snap.specimenType}` : ""}
                </span>
                {r.sampleId && <span className="lab-sample-id" style={{ fontSize: 13 }} title="Sample ID">{r.specimenLabel || r.sampleId}</span>}
                {r.sampleCollectedAt && <span className="lab-muted">collected {fmtDateTime(r.sampleCollectedAt)}</span>}
              </div>
              <Button size="sm" variant="ghost" loading={trendBusy} onClick={loadTrend} disabled={!!trend}>
                {trend ? "Previous values shown" : "Show previous values"}
              </Button>
            </div>

            {r.sampleRejected && (
              <div className="lab-section" style={{ borderColor: "#d64545", background: "#fff3f3" }}>
                <b className="lab-danger-text">Specimen rejected{rejection ? ` — ${rejection}` : ""}.</b>
                <span className="lab-muted"> Entry and verification are blocked until the tube is re-collected from the booking's Sample panel.</span>
              </div>
            )}
            {r.missingRequired?.length > 0 && r.status !== "PUBLISHED" && (
              <div className="lab-muted">Required and still empty: <b>{r.missingRequired.join(", ")}</b></div>
            )}
            {errors && (
              <div className="lab-section" style={{ borderColor: "#d64545" }}>
                {errors.map((e, i) => <div key={i} className="lab-danger-text">{typeof e === "string" ? e : `${e.code ? e.code + ": " : ""}${e.error || e.message || JSON.stringify(e)}`}</div>)}
              </div>
            )}

            <table className="lab-sheet">
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Parameter</th>
                  <th>Result</th>
                  <th>Unit</th>
                  <th>Reference</th>
                  <th>Flag</th>
                  {trend && <th>Previous</th>}
                </tr>
              </thead>
              <tbody>
                {params.map((g) => (
                  <React.Fragment key={g.section || "_"}>
                    {g.section && <tr className="section"><td colSpan={trend ? 6 : 5}>{g.section}</td></tr>}
                    {g.items.map((p) => {
                      const v = valueOf(p.code);
                      const flag = v?.flag || "";
                      const critical = flag === "LL" || flag === "HH";
                      const calc = p.resultType === "calculated";
                      const prev = trend?.[p.code]?.slice(-1)[0];
                      return (
                        <tr key={p.code} className={critical ? "critical" : ""}>
                          <td>
                            {p.name}{p.isRequired && !calc && <span className="req">*</span>}
                            <div className="lab-muted" style={{ fontSize: 11 }}>{p.code}{p.method ? ` · ${p.method}` : ""}</div>
                          </td>
                          <td className={calc ? "calc" : ""}>
                            {calc ? (v?.displayValue ?? (v?.value ?? "—")) : !canEnter ? (
                              <span>{v?.displayValue ?? v?.value ?? <span className="lab-muted">—</span>}</span>
                            ) : p.resultType === "select" ? (
                              <select className="lab-select sm" value={draft[p.code] ?? ""} onChange={(e) => setVal(p.code, e.target.value)}>
                                <option value="">—</option>
                                {(p.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input
                                className="lab-input sm"
                                type={p.resultType === "numeric" ? "number" : "text"}
                                step="any"
                                value={draft[p.code] ?? ""}
                                onChange={(e) => setVal(p.code, e.target.value)}
                              />
                            )}
                          </td>
                          <td className="lab-muted">{p.unit}</td>
                          <td className="lab-muted">{rangeText(p.referenceRange)}</td>
                          <td>{flag ? <span className={`lab-flag ${flag}`} title={FLAG_LABEL[flag]}>{flag}</span> : ""}</td>
                          {trend && (
                            <td className="prev">
                              {prev ? <>{prev.value}{prev.flag && prev.flag !== "N" ? ` (${prev.flag})` : ""}<br /><span>{fmtDate(prev.at)}</span></> : "—"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {snap.reportNotes && <div className="lab-muted">{snap.reportNotes}</div>}

            <div className="lab-grid cols-2">
              <Field label="Technician comments">
                <textarea className="lab-textarea w-full" value={comments} disabled={!canEnter} onChange={(e) => { setComments(e.target.value); setDirty(true); }} />
              </Field>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Field label="Sample collected on">
                  <input type="date" className="lab-input" value={sampleAt} disabled={!canEnter} onChange={(e) => { setSampleAt(e.target.value); setDirty(true); }} />
                </Field>
                {(canSign || r.interpretation) && (
                  <Field label="Interpretation (printed on the report)">
                    <textarea className="lab-textarea w-full" value={interp} disabled={!canSign || r.status === "PUBLISHED"} onChange={(e) => setInterp(e.target.value)} />
                  </Field>
                )}
              </div>
            </div>

            <Section title="History">
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                {[...(r.history || [])].reverse().map((h, i) => (
                  <div key={i} className="lab-row">
                    <Badge kind={h.action}>{cap(h.action)}</Badge>
                    <span className="lab-muted">{fmtDateTime(h.at)} · {h.byName || "—"}{h.note ? ` · ${h.note}` : ""}</span>
                  </div>
                ))}
                {r.pathologyRecordId && <div className="lab-muted">Patient record: <code>{r.pathologyRecordId}</code></div>}
              </div>
            </Section>
          </>
        )}
      </Modal>

      <ReasonModal
        open={!!reasonFor}
        title={reasonFor === "unverify" ? "Send back for entry" : "Cancel result"}
        onClose={() => setReasonFor(null)}
        busy={!!busy}
        onSubmit={doReason}
      />
    </>
  );
};

export default ResultSheet;
